import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, PlayCircle, CheckCircle2, Lock, ArrowUpRight } from 'lucide-react';
import api from '../lib/api';
import { Spinner } from '../components/ui/Spinner';
import { fmtDuration } from '../lib/utils';
import { UnlockInfoModal } from '../components/UnlockInfoModal';

function LessonRow({ lesson }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/app/lessons/${lesson.lesson_id}`)}
      data-testid={`mentorship-lesson-${lesson.lesson_id}`}
      className="flex w-full items-center gap-4 rounded-xl border border-white/5 bg-ink-surface px-4 py-3 text-left transition-colors hover:border-white/20"
    >
      <div className="text-zinc-500">
        {lesson.completed ? (
          <CheckCircle2 className="text-emerald" size={20} />
        ) : lesson.locked ? (
          <Lock size={18} className="text-emerald" />
        ) : (
          <PlayCircle size={20} />
        )}
      </div>
      <div className="relative aspect-video w-24 shrink-0 overflow-hidden rounded-lg bg-ink-elevated">
        {lesson.thumbnail_url ? (
          <img src={lesson.thumbnail_url} alt={lesson.title} className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full place-items-center text-zinc-600"><PlayCircle size={18} /></div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-white">{lesson.title}</div>
        <div className="tnum text-xs text-zinc-500">{fmtDuration(lesson.duration_seconds)} • {lesson.difficulty}</div>
      </div>
    </button>
  );
}

function LockedScreen() {
  const navigate = useNavigate();
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div className="mx-auto max-w-md py-16 text-center" data-testid="mentorship-locked">
      <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-emerald/10 text-emerald">
        <Lock size={28} />
      </div>
      <h1 className="mt-6 font-heading text-2xl font-bold tracking-tight text-white">Mentorship locked</h1>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">
        This course is part of the Mentorship track. Unlock it to start watching.
      </p>
      <div className="mt-8 space-y-3">
        <button
          onClick={() => setShowInfo(true)}
          data-testid="mentorship-locked-unlock"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald py-3 text-sm font-bold text-black transition-transform hover:scale-[1.02]"
        >
          Unlock Mentorship <ArrowUpRight size={16} />
        </button>
        <button
          onClick={() => navigate('/app/mentorship')}
          className="mx-auto flex items-center gap-2 pt-2 text-sm text-zinc-500 hover:text-white"
          data-testid="mentorship-locked-back"
        >
          <ChevronLeft size={14} /> Back to Mentorship
        </button>
      </div>
      <UnlockInfoModal open={showInfo} onClose={() => setShowInfo(false)} showDiscord={false} />
    </div>
  );
}

export default function MentorshipDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/mentorship/courses/${courseId}`)
      .then(({ data }) => setCourse(data.course))
      .catch((e) => {
        if (e?.response?.status === 403) setLocked(true);
      })
      .finally(() => setLoading(false));
  }, [courseId]);

  if (loading)
    return (
      <div className="grid h-[60vh] place-items-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  if (locked) return <LockedScreen />;
  if (!course) return null;

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <button onClick={() => navigate('/app/mentorship')} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white" data-testid="mentorship-back">
        <ChevronLeft size={16} /> All mentorship courses
      </button>

      <div className="overflow-hidden rounded-3xl border border-white/10 bg-ink-card">
        {course.cover_url && (
          <div className="relative aspect-[3/1]">
            <img src={course.cover_url} alt={course.name} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-card to-transparent" />
          </div>
        )}
        <div className="p-8">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white">{course.name}</h1>
          <p className="mt-2 text-zinc-400">{course.description}</p>
          <div className="mt-3 tnum text-sm text-emerald">
            {course.completed_count}/{course.lesson_count} lessons complete
          </div>
        </div>
      </div>

      {course.sections?.map((s) => (
        <section key={s.section_id} className="space-y-3">
          <h2 className="font-heading text-lg font-semibold text-white">{s.name}</h2>
          <div className="space-y-2">
            {s.lessons.map((l) => (
              <LessonRow key={l.lesson_id} lesson={l} />
            ))}
          </div>
        </section>
      ))}

      {course.unsectioned_lessons?.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-heading text-lg font-semibold text-white">Lessons</h2>
          <div className="space-y-2">
            {course.unsectioned_lessons.map((l) => (
              <LessonRow key={l.lesson_id} lesson={l} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
